/**
 * documentName - turning an uploaded filename into something readable.
 *
 * Review documents arrive named the way they were on someone's disk:
 * `08_logistics_provider_fastfreight.docx`. A list of those is a list of
 * slugs, and the leading number - which is genuinely useful ordering
 * information - is buried in the middle of the string.
 *
 * So the name is split into two parts the row renders separately: the number
 * (into the status glyph) and a human title (into the row). Nothing is
 * discarded - the caller keeps the original filename as the row's tooltip.
 *
 * Pure and separate from the component so the parsing is testable on its own;
 * filenames are user data and this has to cope with whatever arrives.
 */

/** The two halves of a parsed filename. */
export interface DocumentLabel {
  /**
   * Leading digits exactly as written, so `08` stays `08`. Null when the
   * filename has no numeric prefix - the row shows a glyph instead.
   */
  number: string | null;
  /** Human-readable title. Never empty: falls back to the filename. */
  title: string;
}

/** Looks like a file extension - short and alphanumeric. */
const EXTENSION = /\.[A-Za-z0-9]{1,5}$/;

/** Leading digits followed by a separator: `08_`, `3 - `, `12.` */
const NUMBER_PREFIX = /^(\d{1,4})\s*[._\-–—)]+\s*/;

/**
 * Capitalise a word, but leave one that already carries capitals alone.
 *
 * `fastfreight` → `Fastfreight`, and `FastFreight` stays `FastFreight`. There
 * is no way to recover intentional inner capitals from an all-lowercase
 * filename, so this preserves them where the author supplied them and does
 * not invent them where they did not.
 */
function capitalise(word: string): string {
  if (!word) return word;
  if (/[A-Z]/.test(word.slice(1))) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function documentLabel(fileName: string | null | undefined): DocumentLabel {
  const raw = (fileName || '').trim();
  if (!raw) return { number: null, title: '' };

  // Extension off first, so `.docx` never becomes part of the title.
  const withoutExtension = raw.replace(EXTENSION, '');

  const match = withoutExtension.match(NUMBER_PREFIX);
  const number = match ? match[1] : null;
  const remainder = match ? withoutExtension.slice(match[0].length) : withoutExtension;

  const title = remainder
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(capitalise)
    .join(' ');

  return {
    number,
    // A name that is ONLY a number (`08.docx`) leaves nothing to title, and a
    // blank row is worse than a technical one - fall back rather than invent.
    title: title || withoutExtension || raw,
  };
}

export default documentLabel;
